import { Close, Star } from "@mui/icons-material";
import { Box, List, ListItem, Typography } from "@mui/material";
import { chain, partition, sumBy } from "lodash";
import { ReactElement, useMemo } from "react";

import { FlexBox } from "~frontend/components/FlexBox";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import { formatContactData } from "~frontend/util/contactFields";
import { plural } from "~frontend/util/plural";

import { SectionWrapper } from ".";
import { CampaignViewAccordion } from "./CampaignViewAccordion";
import { ScriptPreview } from "./ScriptPreview";
import { shouldShowClicks } from "./shouldShowClicks";
import DetailStatsDrawer from "./stats/DetailStatsDrawer";
import TopLineStats from "./stats/TopLineStats";
import {
  CampaignStatsForDetailQueryResult,
  StatsQueryData,
  StatsQueryResult,
} from "./stats/useCampaignStats";
import { CampaignForView } from "./types";

type VariantDetailRow = {
  id: string;
  order: number;
  script: string;
  deletedAt: string | null;
  mediaUpload?: CampaignForView["scriptVariants"][0]["mediaUpload"];
  mediaContactFieldName?: CampaignForView["scriptVariants"][0]["mediaContactFieldName"];
  overrideLinkShortenerSettings: boolean;
  linkShortenerDomainName?: string | null;
  stats: StatsQueryResult;
  personalizedGif: CampaignForView["scriptVariants"][0]["personalizedGif"];
};

const VariantStatistics = ({
  rows,
  campaign,
  showFundraisingFields = false,
  title,
}: {
  rows: VariantDetailRow[];
  campaign: CampaignForView;
  showFundraisingFields?: boolean;
  title?: string;
}): ReactElement => {
  const subaccount = useSubaccountAdminConfig()!;
  const roiTestCompleteEvent = useMemo(
    () =>
      campaign.events.find(
        (
          event
        ): event is Extract<
          CampaignForView["events"][number],
          { __typename?: "CampaignInitialSendRoiTestWinnerChosenEvent" }
        > => event.__typename === "CampaignInitialSendRoiTestWinnerChosenEvent"
      ),
    [campaign]
  );
  const roiWinnerId = roiTestCompleteEvent?.data.bestScriptVariant.id;
  const roiLoserIds = useMemo(
    () =>
      new Set(
        roiTestCompleteEvent?.data.deletedScriptVariants.map(
          variant => variant.id
        )
      ),
    [roiTestCompleteEvent]
  );
  const excludedDonationsAmountsByVariantId = useMemo(() => {
    if (!roiTestCompleteEvent) {
      return {};
    }

    return chain(roiTestCompleteEvent.data.excludedDonations)
      .groupBy(donation => donation.scriptVariantId)
      .mapValues(donations =>
        chain(donations)
          .map(donation => donation.donationAmountInCents)
          .countBy()
          .map((count, donationAmountInCents) => ({
            count,
            donationAmountInCents: parseFloat(donationAmountInCents),
          }))
          .value()
      )
      .value();
  }, [roiTestCompleteEvent]);

  return (
    <Box>
      {title && (
        <Typography
          variant="h1Bold"
          sx={{
            marginBottom: theme.spacing(2),
          }}
          component="h2"
        >
          {title}
        </Typography>
      )}
      {rows.map((row, idx) => {
        const showClicks = shouldShowClicks(
          {
            ...row,
            clickCount:
              row.stats.state === "loaded" ? row.stats.data.clickCount : 0,
          },
          subaccount
        );

        return (
          <Box
            key={`variantStats${idx}`}
            data-testid={`variant-stats-${row.id}`}
            sx={{
              marginBottom: theme.spacing(2),
            }}
          >
            <FlexBox
              justifyContent="space-between"
              sx={{
                borderBottom: `2px solid ${palette.lightGray}`,
                marginBottom: theme.spacing(2),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: theme.spacing(1),
                  alignItems: "center",
                }}
              >
                {row.id === roiWinnerId && <RoiTestIcon variant="winner" />}
                {roiLoserIds.has(row.id) && <RoiTestIcon variant="loser" />}
                <Typography variant="label2Caps" component="span">
                  Script Variant {idx + 1}
                </Typography>
              </Box>
              {row.id === roiWinnerId && <RoiTestLabel variant="winner" />}
              {roiLoserIds.has(row.id) && <RoiTestLabel variant="loser" />}
            </FlexBox>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
              }}
            >
              <Box sx={{ width: "50%", paddingRight: "30px" }}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: "20px 20px 20px 0",
                    backgroundColor: palette.lightBlue,
                  }}
                >
                  <ScriptPreview
                    campaignId={campaign.id}
                    variant={row}
                    sampleContactData={
                      formatContactData({
                        contactData: JSON.parse(
                          campaign.sampleContact?.data || "{}"
                        ),
                        campaignContactFields: campaign.contactFields,
                      }).formattedData
                    }
                  />
                </Box>
              </Box>
              <Box sx={{ width: "50%" }}>
                <TopLineStats
                  stats={row.stats}
                  showFundraisingFields={showFundraisingFields}
                  showClicks={showClicks}
                  usingOptInStrategy={campaign.usingOptInStrategy}
                />
                {roiLoserIds.has(row.id) &&
                  (excludedDonationsAmountsByVariantId?.[row.id]?.length ?? 0) >
                    0 && (
                    <Box
                      sx={{
                        backgroundColor: palette.lightOrange,
                        padding: "10px 16px",
                        margin: "24px 0",
                      }}
                    >
                      <Typography variant="body3">
                        <Typography variant="body3Bold">
                          Omitted from Conditional ROI Test:
                        </Typography>{" "}
                        {plural(
                          sumBy(
                            excludedDonationsAmountsByVariantId[row.id],
                            "count"
                          ),
                          "donation",
                          "donations"
                        )}{" "}
                        over{" "}
                        {(
                          campaign.roiTestConfig!.excludeDonationsOverCents! /
                          100.0
                        ).toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                          minimumFractionDigits: 2,
                        })}
                      </Typography>
                      <List sx={{ padding: 0 }}>
                        {excludedDonationsAmountsByVariantId[row.id].map(
                          ({ count, donationAmountInCents }) => (
                            <ListItem sx={{ padding: "0 24px" }}>
                              <Typography variant="body3">
                                {plural(count, "Donation", "Donations")} of{" "}
                                {(donationAmountInCents / 100.0).toLocaleString(
                                  "en-US",
                                  { style: "currency", currency: "USD" }
                                )}
                              </Typography>
                            </ListItem>
                          )
                        )}
                      </List>
                    </Box>
                  )}
                <Box
                  sx={{
                    flexDirection: "row",
                    marginLeft: "10px",
                  }}
                >
                  <DetailStatsDrawer
                    stats={row.stats}
                    showFundraisingFields={showFundraisingFields}
                    showClicks={showClicks}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        );
      })}
      <Typography
        variant="body2"
        sx={{
          color: palette.navy,
          textAlign: "right",
          marginTop: theme.spacing(3),
          borderTop: `2px solid ${palette.lightGray}`,
          paddingTop: theme.spacing(1),
        }}
        component={"div"}
      >
        The script preview contact fields are based on the sample contact chosen
        in the Contact Upload step.
      </Typography>
    </Box>
  );
};

const RoiTestIcon = ({
  variant,
}: {
  variant: "winner" | "loser";
}): ReactElement => {
  const IconComponent = variant === "winner" ? Star : Close;
  const backgroundColor =
    variant === "winner" ? palette.midGreen : palette.midOrange;
  const iconColor = variant === "winner" ? palette.darkGreen : palette.orange;

  return (
    <Box
      sx={{
        display: "inline-block",
        position: "relative",
        height: "22px",
        width: "22px",
        borderRadius: "11px",
        marginRight: theme.spacing(1),
        backgroundColor,
      }}
    >
      <IconComponent
        fontSize="small"
        sx={{
          margin: 0,
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          color: iconColor,
        }}
      />
    </Box>
  );
};

const RoiTestLabel = ({
  variant,
}: {
  variant: "winner" | "loser";
}): ReactElement => {
  const backgroundColor =
    variant === "winner" ? palette.midGreen : palette.midOrange;
  const textColor = variant === "winner" ? palette.darkGreen : palette.orange;

  return (
    <Box>
      <Typography
        variant="body2Bold"
        sx={{
          color: textColor,
          backgroundColor,
          padding: "0 4px",
        }}
      >
        {variant === "winner"
          ? "Winner of Conditional ROI Test"
          : "Loser of Conditional ROI Test"}
      </Typography>
    </Box>
  );
};

export const CurrentAndPastVariantStatistics = ({
  campaign,
  stats,
  showFundraisingFields = false,
}: {
  campaign: CampaignForView;
  stats: CampaignStatsForDetailQueryResult;
  showFundraisingFields: boolean;
}): ReactElement => {
  const rows: VariantDetailRow[] =
    campaign.scriptVariants.map((variant, idx) => {
      let variantStats: StatsQueryResult;
      if (stats.state === "loading") {
        variantStats = { state: "loading" };
      } else if (stats.state === "error") {
        variantStats = { state: "error" };
      } else {
        const data =
          stats.data.variants.find(
            variantStats => variantStats.variantId === variant.id
          ) ??
          ({
            __typename: "CampaignStatsForDetailVariant",
            clickCount: 0,
            contactCount: 0,
            contactsAutomaticOptedOutCount: 0,
            contactsDidReplyCount: 0,
            contactsOptedOutCount: 0,
            donationCount: 0,
            initialCostInMillicents: 0,
            initialMessageFilterCount: 0,
            initialMessageMaybeFilterCount: 0,
            maybeFilteredThreshold: 0,
            recurringDonationCount: 0,
            totalRaisedInCents: 0,
            totalRefundedInCents: 0,
            totalRecurringRaisedInCents: 0,
            variantId: variant.id,
          } satisfies StatsQueryData);

        variantStats = { state: "loaded", data };
      }

      return {
        id: variant.id,
        order: variant.order || idx,
        script: variant.script,
        deletedAt: variant.deletedAt,
        mediaUpload: variant.mediaUpload,
        mediaContactFieldName: variant.mediaContactFieldName,
        overrideLinkShortenerSettings: variant.overrideLinkShortenerSettings,
        linkShortenerDomainName: variant.linkShortenerDomainName,
        personalizedGif: variant.personalizedGif,
        stats: variantStats,
      };
    }) ?? [];

  const [currentVariants, pastVariants] = partition(
    rows,
    variant => !variant.deletedAt
  );

  const pastVariantsWithSends = pastVariants.filter(
    variant =>
      variant.stats.state === "loaded" && variant.stats.data.contactCount > 0
  );

  return (
    <>
      <SectionWrapper sx={{ backgroundColor: palette.white }}>
        <VariantStatistics
          rows={currentVariants}
          campaign={campaign}
          showFundraisingFields={showFundraisingFields}
          title="Script Variants"
        />
      </SectionWrapper>

      {pastVariantsWithSends.length > 0 && (
        <CampaignViewAccordion
          header="Past Script Variants"
          sx={{ backgroundColor: palette.white }}
        >
          <VariantStatistics
            rows={pastVariantsWithSends}
            campaign={campaign}
            showFundraisingFields={showFundraisingFields}
          />
        </CampaignViewAccordion>
      )}
    </>
  );
};
